import React from "react";

import {
  DEFAULT_LOCALE,
  getLocaleDefinition,
  resolveInitialLocale,
  saveStoredLocale,
  SUPPORTED_LOCALES,
} from "./locales";
import { translations } from "./translations";
import { vibePassTranslations } from "./vibePassTranslations";

const I18nContext = React.createContext(null);

const customerTranslations = Object.fromEntries(
  Object.entries(translations).map(([locale, entries]) => [
    locale,
    { ...entries, ...(vibePassTranslations[locale] || {}) },
  ])
);

customerTranslations.sv = { ...translations.en, ...vibePassTranslations.sv };

export function createTranslator(locale, dictionary = customerTranslations) {
  const active = dictionary[locale] || dictionary[DEFAULT_LOCALE] || {};
  const fallback = dictionary[DEFAULT_LOCALE] || {};

  return function t(key, values = {}) {
    const template = active[key] || fallback[key] || "";

    return String(template).replace(/\{(\w+)\}/g, (_, token) =>
      Object.prototype.hasOwnProperty.call(values, token) ? String(values[token]) : ""
    );
  };
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = React.useState(() => resolveInitialLocale());

  React.useEffect(() => {
    const definition = getLocaleDefinition(locale);
    document.documentElement.lang = definition.htmlLang;
  }, [locale]);

  const setLocale = React.useCallback((nextLocale) => {
    setLocaleState(saveStoredLocale(nextLocale));
  }, []);

  const value = React.useMemo(
    () => ({
      locale,
      localeDefinition: getLocaleDefinition(locale),
      locales: SUPPORTED_LOCALES,
      setLocale,
      t: createTranslator(locale),
    }),
    [locale, setLocale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = React.useContext(I18nContext);

  if (!context) {
    return {
      locale: DEFAULT_LOCALE,
      localeDefinition: getLocaleDefinition(DEFAULT_LOCALE),
      locales: SUPPORTED_LOCALES,
      setLocale: () => {},
      t: createTranslator(DEFAULT_LOCALE),
    };
  }

  return context;
}
